<template>
  <div class="bg-primary-50 p-4 md:p-8">
    <div
      id="svg-plan"
      ref="svgScheme"
      class="relative flex min-h-fit w-full flex-col items-center overflow-hidden overscroll-contain"
      v-html="svgSchemeContent"
    />
  </div>
</template>

<script setup>
import useTranslations from '@/Front/Composables/useTranslations';
import { useApp } from '@/Front/Stores/useApp.js';
import { onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import { useTippy } from 'vue-tippy';

const emits = defineEmits(['select-sector']);

const appStore = useApp();
const i18n = useTranslations();

const svgScheme = useTemplateRef('svgScheme');
const svgSchemeContent = ref(null);
const tippyInstances = [];
let delegatedClickHandler = null;
async function loadSvgScheme() {
  if (!appStore.svgScheme) return;

  try {
    const { data } = await axios.get(appStore.svgScheme, { responseType: 'text' });
    svgSchemeContent.value = data;
  } catch {
    svgSchemeContent.value = null;
  }
}

function getOpacity(hasTickets, hasLink) {
  return !hasTickets || !hasLink ? 0.1 : 1;
}

function fixSvgRootSize(root) {
  if (!root) {
    return;
  }
  try {
    root.setAttribute('width', '100%');
    root.setAttribute('height', '100%');
    if (root.style) {
      root.style.width = '100%';
      root.style.height = '100%';
    }
  } catch (e) {
    void e;
  }
}

onMounted(async () => {
  const visualPlanData = appStore.visualPlanData ?? {};

  await loadSvgScheme();

  const svgRoot = svgScheme.value.querySelector('svg');
  fixSvgRootSize(svgRoot);

  svgRoot.querySelectorAll('[data-href],[data-exthref]').forEach((area) => {
    const sectorId = area.getAttribute('data-href') || area.getAttribute('data-exthref') || null;
    const title = visualPlanData?.[sectorId]?.name ?? null;
    const prices = visualPlanData?.[sectorId]?.prices ?? null;
    const hasTickets = (visualPlanData?.[sectorId]?.quantity ?? 0) !== 0;
    const link = visualPlanData?.[sectorId]?.link ?? null;

    const objectTitleParts = [];
    if (title) {
      objectTitleParts.push(`<strong>${i18n.trans(title)}</strong>`);
    }

    if (prices) {
      objectTitleParts.push(`${i18n.trans('Цена:')} ${prices}`);
    }
    const objectTitle = objectTitleParts.join('<br>');

    area.setAttribute('fill-opacity', getOpacity(hasTickets, !!link));

    if (hasTickets && link) {
      area.setAttribute('data-tippy-content', objectTitle);
      area.classList.add('cursor-pointer');

      tippyInstances.push(
        useTippy(area, {
          allowHTML: true,
          ignoreAttributes: false,
          followCursor: true,
        }),
      );
    }
  });

  delegatedClickHandler = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Find the closest element that carries the sector dataset
    const clickable = target.closest('[data-href],[data-exthref]');
    if (!clickable || !svgRoot.contains(clickable)) return;

    const sectorId = clickable.getAttribute('data-href') || clickable.getAttribute('data-exthref') || null;
    if (!sectorId) return;

    const isGA = visualPlanData?.[sectorId]?.isGA ?? true;
    const hasTickets = (visualPlanData?.[sectorId]?.quantity ?? 0) !== 0;
    const link = visualPlanData?.[sectorId]?.link ?? null;

    // Only emit when it is actually selectable
    if (hasTickets && link) {
      event.preventDefault();

      emits('select-sector', sectorId, isGA);
    }
  };

  svgScheme.value.addEventListener('click', delegatedClickHandler);
});

onUnmounted(() => {
  tippyInstances.forEach((tippy) => tippy.destroy());

  if (svgScheme.value && delegatedClickHandler) {
    svgScheme.value.removeEventListener('click', delegatedClickHandler);
  }

  delegatedClickHandler = null;
});
</script>
